import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../utils/api";
import { toast } from "react-toastify";
import { formatDate } from "../../utils/helpers";
import AdminNav from "../../components/AdminNav";
import { normalizePaymentMethods } from "../../utils/paymentMethods";
import { normalizeShippingMethods } from "../../utils/shippingMethods";
import { getSelectedOptionsList } from "../../utils/productOptions";

const UserManagement = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revokeReason, setRevokeReason] = useState("");
  const [isRevoking, setIsRevoking] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updatingOrderStatus, setUpdatingOrderStatus] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [selectedPaymentMethodIds, setSelectedPaymentMethodIds] = useState([]);
  const [selectedShippingMethodIds, setSelectedShippingMethodIds] = useState([]);
  const [savingMethodPermissions, setSavingMethodPermissions] = useState(false);

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const response = await api.get(`/admin/users/${userId}/orders`);
      setOrders(response.data.orders);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setOrdersLoading(false);
    }
  }, [userId]);

  const fetchUser = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/users/${userId}`);
      setUser(response.data.user);
      setSelectedPaymentMethodIds(response.data.user.allowed_payment_method_ids || []);
      setSelectedShippingMethodIds(response.data.user.allowed_shipping_method_ids || []);
      
      // Fetch orders if user is approved
      if (response.data.user.status === 'approved') {
        fetchOrders();
      }
    } catch (error) {
      toast.error("Failed to fetch user details");
      console.error("Error fetching user:", error);
    } finally {
      setLoading(false);
    }
  }, [userId, fetchOrders]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    const fetchMethodSettings = async () => {
      try {
        const { data } = await api.get("/admin/settings/site");
        const nextPaymentMethods = normalizePaymentMethods(data.settings?.payment_methods || []);
        const nextShippingMethods = normalizeShippingMethods(data.settings?.shipping_methods || []);

        setPaymentMethods(nextPaymentMethods);
        setShippingMethods(nextShippingMethods);
      } catch (error) {
        console.error("Error fetching method settings:", error);
      }
    };

    fetchMethodSettings();
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (paymentMethods.length > 0 && (!user.allowed_payment_method_ids || user.allowed_payment_method_ids.length === 0)) {
      setSelectedPaymentMethodIds(paymentMethods.map((method) => method.id));
    }

    if (shippingMethods.length > 0 && (!user.allowed_shipping_method_ids || user.allowed_shipping_method_ids.length === 0)) {
      setSelectedShippingMethodIds(shippingMethods.map((method) => method.id));
    }
  }, [paymentMethods, shippingMethods, user]);

  const handleRevokeAccess = async () => {
    if (!revokeReason.trim()) {
      toast.error("Please provide a reason for revoking access");
      return;
    }

    setIsRevoking(true);
    try {
      await api.put(`/admin/users/${userId}/revoke`, { reason: revokeReason });
      toast.success("Access revoked successfully. User has been notified via email.");
      setRevokeReason("");
      fetchUser(); // Refresh user data
    } catch (error) {
      toast.error("Failed to revoke access");
      console.error("Error revoking access:", error);
    } finally {
      setIsRevoking(false);
    }
  };

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      await api.put(`/admin/users/${userId}/approve`, {
        allowed_payment_method_ids: selectedPaymentMethodIds,
        allowed_shipping_method_ids: selectedShippingMethodIds,
      });
      toast.success("User approved successfully. User has been notified via email.");
      fetchUser(); // Refresh user data
    } catch (error) {
      toast.error("Failed to approve user");
      console.error("Error approving user:", error);
    } finally {
      setIsApproving(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: "bg-green-500 text-black",
      pending: "bg-yellow-500 text-black",
      rejected: "bg-red-500 text-white",
      revoked: "bg-red-700 text-white",
    };
    return <span className={`badge ${styles[status] || "bg-gray-500 text-white"}`}>{status}</span>;
  };

  const toggleMethodSelection = (methodId, selectedIds, setSelectedIds) => {
    setSelectedIds((current) =>
      current.includes(methodId)
        ? current.filter((id) => id !== methodId)
        : [...current, methodId]
    );
  };

  const saveMethodPermissions = async () => {
    setSavingMethodPermissions(true);
    try {
      const response = await api.put(`/admin/users/${userId}/method-permissions`, {
        allowed_payment_method_ids: selectedPaymentMethodIds,
        allowed_shipping_method_ids: selectedShippingMethodIds,
      });
      setUser(response.data.user);
      toast.success("User method permissions updated");
    } catch (error) {
      console.error("Error saving user method permissions:", error);
      toast.error(error.response?.data?.error || "Failed to update user method permissions");
    } finally {
      setSavingMethodPermissions(false);
    }
  };

  const openOrderDetails = async (orderId) => {
    try {
      const response = await api.get(`/orders/admin/${orderId}`);
      setSelectedOrder(response.data.order);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to load order details");
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setUpdatingOrderStatus(true);
      await api.put(`/orders/admin/${orderId}/status`, { status: newStatus });
      toast.success("Order status updated successfully");

      setOrders((currentOrders) => currentOrders.map((order) => (
        order.id === orderId ? { ...order, status: newStatus } : order
      )));

      setSelectedOrder((currentOrder) => (
        currentOrder?.id === orderId ? { ...currentOrder, status: newStatus } : currentOrder
      ));

      await fetchOrders();
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update order status");
    } finally {
      setUpdatingOrderStatus(false);
    }
  };

  const getOrderPaymentStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-500/20 text-yellow-400",
      completed: "bg-green-500/20 text-green-400",
      failed: "bg-red-500/20 text-red-400",
    };

    return colors[status] || "bg-gray-500/20 text-gray-400";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <AdminNav />
        <div className="container mx-auto px-4 py-6 sm:py-8">
          <div className="text-center text-gold-500">Loading user details...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-black">
        <AdminNav />
        <div className="container mx-auto px-4 py-6 sm:py-8">
          <div className="text-center text-gray-400">User not found</div>
          <div className="text-center mt-4">
            <button onClick={() => navigate("/admin/users")} className="btn-secondary">
              Back to Users
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <AdminNav />
      <div className="container mx-auto px-4 py-6 sm:py-8">
        <button onClick={() => navigate("/admin/users")} className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-gold-500 transition-colors duration-200 hover:text-gold-300">
          ← Back to Users
        </button>
        <div className="mb-6">
          <h1 className="heading-metallic text-3xl">User Management</h1>
        </div>

        {/* User Details Card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card mb-6">
          <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-white">
                {user.first_name} {user.last_name}
              </h2>
              <p className="text-gray-400">{user.email}</p>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <span className={`badge ${user.role === "admin" ? "badge-gold" : "badge-outline"}`}>{user.role}</span>
              {getStatusBadge(user.status)}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-2">Contact Information</h3>
              <p className="text-gray-300">
                <strong>Phone:</strong> {user.phone || "N/A"}
              </p>
              <p className="text-gray-300">
                <strong>Company:</strong> {user.company_name || "N/A"}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-2">Delivery Address</h3>
              <p className="text-gray-300">
                {user.address_line1 && (
                  <>
                    {user.address_line1}
                    <br />
                  </>
                )}
                {user.address_line2 && (
                  <>
                    {user.address_line2}
                    <br />
                  </>
                )}
                {user.city && `${user.city}, `}
                {user.state && `${user.state}, `}
                {user.country}
              </p>
              {user.postal_code && <p className="text-gray-300">{user.postal_code}</p>}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-2">Billing Address</h3>
              <p className="text-gray-300">
                {user.billing_address_line1 && (
                  <>
                    {user.billing_address_line1}
                    <br />
                  </>
                )}
                {user.billing_address_line2 && (
                  <>
                    {user.billing_address_line2}
                    <br />
                  </>
                )}
                {user.billing_city && `${user.billing_city}, `}
                {user.billing_state && `${user.billing_state}, `}
                {user.billing_country}
              </p>
              {user.billing_postal_code && <p className="text-gray-300">{user.billing_postal_code}</p>}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-2">Business Information</h3>
              {user.tax_id && (
                <p className="text-gray-300">
                  <strong>Tax ID:</strong> {user.tax_id}
                </p>
              )}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-2">Account Dates</h3>
              <p className="text-gray-300">
                <strong>Registered:</strong> {formatDate(user.created_at)}
              </p>
              {user.updated_at && (
                <p className="text-gray-300">
                  <strong>Last Updated:</strong> {formatDate(user.updated_at)}
                </p>
              )}
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="card mb-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="heading-metallic-soft text-xl mb-2">Method Permissions</h2>
              <p className="text-sm text-gray-400">Choose which payment methods and shipping methods this user can access.</p>
            </div>
            <button onClick={saveMethodPermissions} disabled={savingMethodPermissions} className="btn-primary w-full md:w-auto">
              {savingMethodPermissions ? "Saving..." : "Save Method Permissions"}
            </button>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-300">Allowed Payment Methods</h3>
              {paymentMethods.length === 0 ? (
                <p className="text-sm text-gray-400">No payment methods configured in Site Settings.</p>
              ) : (
                <div className="space-y-3">
                  {paymentMethods.map((method) => (
                    <label key={method.id} className="flex items-center gap-3 rounded border border-gray-700 bg-black/30 px-4 py-3 text-sm text-white">
                      <input
                        type="checkbox"
                        checked={selectedPaymentMethodIds.includes(method.id)}
                        onChange={() => toggleMethodSelection(method.id, selectedPaymentMethodIds, setSelectedPaymentMethodIds)}
                      />
                      <span>{method.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-300">Allowed Shipping Methods</h3>
              {shippingMethods.length === 0 ? (
                <p className="text-sm text-gray-400">No shipping methods configured in Site Settings.</p>
              ) : (
                <div className="space-y-3">
                  {shippingMethods.map((method) => (
                    <label key={method.id} className="flex items-center gap-3 rounded border border-gray-700 bg-black/30 px-4 py-3 text-sm text-white">
                      <input
                        type="checkbox"
                        checked={selectedShippingMethodIds.includes(method.id)}
                        onChange={() => toggleMethodSelection(method.id, selectedShippingMethodIds, setSelectedShippingMethodIds)}
                      />
                      <span>{method.name}</span>
                      <span className="ml-auto text-xs text-gray-400">{method.duration}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Access Management Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card">
          <h2 className="heading-metallic-soft text-xl mb-4">Access Management</h2>

          {user.status === "approved" ? (
            <div className="space-y-4">
              <p className="text-gray-300">This user currently has active access to the platform.</p>

              <div>
                <label htmlFor="revokeReason" className="block text-sm font-medium text-gray-300 mb-2">
                  Reason for Revoking Access
                </label>
                <textarea
                  id="revokeReason"
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  placeholder="Enter the reason why you are revoking this user's access. This will be included in the notification email sent to the user."
                  className="input-field w-full h-32"
                  disabled={isRevoking}
                />
              </div>

              <button
                onClick={handleRevokeAccess}
                disabled={isRevoking || !revokeReason.trim()}
                className={`bg-red-600 hover:bg-red-500 text-white px-6 py-3 rounded-md transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed ${
                  isRevoking ? "opacity-50" : ""
                }`}
              >
                {isRevoking ? "Revoking Access..." : "Revoke Access"}
              </button>

              <p className="text-sm text-gray-400">
                ⚠️ Revoking access will immediately prevent this user from accessing their account and placing orders. They will receive an email notification with the reason provided above.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-gray-400">
                This user's account status is currently: <strong className="text-white">{user.status}</strong>
              </p>
              {user.status === "revoked" && (
                <p className="text-gray-300">Access has been revoked for this user. They can no longer access the platform.</p>
              )}
              {user.status === "pending" && (
                <>
                  <p className="text-gray-300">This user is awaiting approval. You can approve them to grant access to the platform.</p>
                  <button
                    onClick={handleApprove}
                    disabled={isApproving}
                    className={`btn-primary ${isApproving ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isApproving ? "Approving..." : "Approve User"}
                  </button>
                </>
              )}
              {user.status === "rejected" && (
                <p className="text-gray-300">This user's application was rejected. They cannot access the platform.</p>
              )}
            </div>
          )}
        </motion.div>

        {/* User Orders Section - Only for approved users */}
        {user.status === "approved" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card">
            <h2 className="heading-metallic-soft text-xl mb-4">Order History</h2>
            
            {ordersLoading ? (
              <p className="text-gray-400">Loading orders...</p>
            ) : orders.length === 0 ? (
              <p className="text-gray-400">This user has not placed any orders yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[44rem]">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-gray-300">Order #</th>
                      <th className="text-left py-3 px-4 text-gray-300">Date</th>
                      <th className="text-left py-3 px-4 text-gray-300">Total</th>
                      <th className="text-left py-3 px-4 text-gray-300">Status</th>
                      <th className="text-left py-3 px-4 text-gray-300">Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr
                        key={order.id}
                        onClick={() => openOrderDetails(order.id)}
                        className="border-b border-gray-800 hover:bg-black-lighter cursor-pointer transition-colors"
                      >
                        <td className="py-3 px-4 text-white">{order.order_number}</td>
                        <td className="py-3 px-4 text-gray-300">{formatDate(order.created_at)}</td>
                        <td className="py-3 px-4 text-white">€{Number(order.total ?? order.total_amount ?? 0).toFixed(2)}</td>
                        <td className="py-3 px-4">
                          <span
                            className={`badge ${
                              order.status === "completed"
                                ? "bg-green-500 text-black"
                                : order.status === "pending"
                                ? "bg-yellow-500 text-black"
                                : order.status === "processing"
                                ? "bg-blue-500 text-white"
                                : "bg-gray-500 text-white"
                            }`}
                          >
                            {order.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`badge ${
                              order.payment_status === "paid"
                                ? "bg-green-500 text-black"
                                : order.payment_status === "pending"
                                ? "bg-yellow-500 text-black"
                                : "bg-red-500 text-white"
                            }`}
                          >
                            {order.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-4 text-sm text-gray-400">
                  Total Orders: {orders.length}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            onClick={() => setSelectedOrder(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-gradient-to-r from-black via-gray-900 to-black"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 z-10 border-b border-gray-700 bg-gradient-to-r from-black via-gray-900 to-black p-6">
                <div className="flex items-start justify-between gap-4">
                  <h2 className="heading-metallic-soft text-2xl">Order #{selectedOrder.order_number}</h2>
                  <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-white">
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="space-y-6 p-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <h3 className="heading-metallic-soft mb-3 text-lg">Customer Information</h3>
                    <div className="space-y-2 text-sm">
                      <p>
                        <span className="text-gray-400">Name:</span>
                        <span className="ml-2">{[selectedOrder.first_name, selectedOrder.last_name].filter(Boolean).join(" ") || selectedOrder.company_name || "N/A"}</span>
                      </p>
                      <p>
                        <span className="text-gray-400">Email:</span>
                        <span className="ml-2">{selectedOrder.user_email || "N/A"}</span>
                      </p>
                      <p>
                        <span className="text-gray-400">Order Date:</span>
                        <span className="ml-2">{formatDate(selectedOrder.created_at)}</span>
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="heading-metallic-soft mb-3 text-lg">Order Status</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="mb-2 block text-sm text-gray-400">Current Status:</label>
                        <select
                          value={selectedOrder.status}
                          onChange={(e) => updateOrderStatus(selectedOrder.id, e.target.value)}
                          disabled={updatingOrderStatus}
                          className="input-field w-full"
                        >
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                      <div>
                        <span className="text-sm text-gray-400">Payment Status:</span>
                        <span className={`ml-2 rounded px-2 py-1 text-xs ${getOrderPaymentStatusColor(selectedOrder.payment_status)}`}>{selectedOrder.payment_status}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-400">Payment Method:</span>
                        <span className="ml-2">{selectedOrder.payment_method || "N/A"}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedOrder.shipping_address && (
                  <div>
                    <h3 className="heading-metallic-soft mb-3 text-lg">Shipping Address</h3>
                    <div className="rounded bg-gray-800 p-4 text-sm">
                      <p>{selectedOrder.shipping_address.addressLine1}</p>
                      {selectedOrder.shipping_address.addressLine2 && <p>{selectedOrder.shipping_address.addressLine2}</p>}
                      <p>
                        {selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} {selectedOrder.shipping_address.postalCode}
                      </p>
                      <p>{selectedOrder.shipping_address.country}</p>
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="heading-metallic-soft mb-3 text-lg">Order Items</h3>
                  <div className="space-y-3">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex flex-col gap-3 rounded bg-gray-800 p-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex-1">
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-sm text-gray-400">
                            SKU: {item.product_sku} | Qty: {item.quantity}
                          </p>
                          {getSelectedOptionsList(item.selected_options).map((option) => (
                            <p key={`${option.name}-${option.value}`} className="text-sm text-gray-500">
                              {option.name}: {option.value}
                            </p>
                          ))}
                        </div>
                        <div className="text-left sm:text-right">
                          <p className="font-medium">€{parseFloat(item.price).toFixed(2)}</p>
                          <p className="text-sm text-gray-400">Subtotal: €{(item.quantity * item.price).toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-gray-700 pt-4">
                  <div className="space-y-2 text-right">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Subtotal:</span>
                      <span>€{parseFloat(selectedOrder.subtotal || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Tax:</span>
                      <span>€{parseFloat(selectedOrder.tax || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Shipping:</span>
                      <span>€{parseFloat(selectedOrder.shipping || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-700 pt-2 text-xl font-bold">
                      <span className="text-gold-500">Total:</span>
                      <span className="text-gold-500">€{parseFloat(selectedOrder.total).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {selectedOrder.customer_notes && (
                  <div>
                    <h3 className="heading-metallic-soft mb-3 text-lg">Customer Notes</h3>
                    <div className="rounded bg-gray-800 p-4 text-sm">
                      <p>{selectedOrder.customer_notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserManagement;
